import { ColumnDefinition } from "@k12/admin-components";
import { Contact, DocumentsList, Email, EmployeeSummary, HouseholdStudent, IssueHistory, MessageCenterPreviewData, Task } from "@k12/models";

export const COMMUNICATIONS_CENTER_URL = '/communications-center';
export const MESSAGES_URL = '/messages';
export const TASKS_URL = '/tasks';
export const WORK_QUEUE_URL = '/work-queue';

export const ContactsData: Contact[] = [];
export const DocumentData: DocumentsList[] = [];
export const EmailData: Email[] = [];
export const EmployeesData: EmployeeSummary[] = [];
export const IssueTrackingData: IssueHistory[] = [];
export const MessagesData: MessageCenterPreviewData[] = [];
export const StudentsData: HouseholdStudent[] = []
export const TaskData: Task[] = [];

export const TaskColumnWidths = {
  name: 50,
  dateCreated: 25,
  status: 25,
} as const;

export const EmailColumnWidths = {
  subject: 80,
  startDate: 20,
} as const;

export const DocumentColumnWidths = {
  type: 40,
  version: 30,
  status: 30,
} as const;

export const MessagesColumnWidths = {
  category: 40,
  subCategory: 40,
  date: 20,
} as const;

export const EmployeesColumnWidths = {
  firstName: 20,
  lastName: 20,
  service: 20,
  subService: 40,
} as const;

export const ContactsColumnWidths = {
  employee: 40,
  email: 40,
  isPrimary: 20,
} as const;

export const StudentsDataColumnWidths = {
  studentName: 12,
  pidNumber: 10,
  grade: 10,
  osAwardAmountOffered: 20,
  esaAwardAmountOffered: 15,
  studentStatus: 10,
  schoolName: 23,
} as const;

export const IssueTrackingColumnWidths = {
  chevron: 5,
  date: 10,
  onHoldStatus: 40,
  comments: 30,
  issuedBy: 10,
  action: 5,
} as const;

export const TaskColumnDefinitions: ColumnDefinition[] = [
  {
    name: 'name',
    header: 'Task Name',
    sortable: true,
    width: TaskColumnWidths.name
  },
  {
    name: 'dateCreated',
    header: 'Created Date',
    sortable: true,
    width: TaskColumnWidths.dateCreated
  },
  {
    name: 'taskStatus',
    header: 'Status',
    sortable: true,
    centered: true,
    width: TaskColumnWidths.status,
  }
];

export const EmailColumnDefinitions: ColumnDefinition[] = [
  {
    name: 'subject',
    header: 'Subject',
    sortable: true,
    width: EmailColumnWidths.subject
  },
  {
    name: 'startDate',
    header: 'Date Sent',
    sortable: true,
    width: EmailColumnWidths.startDate
  }
];

export const DocumentColumnDefinitions: ColumnDefinition[] = [
  {
    name: 'documentType',
    header: 'Document Type',
    sortable: true,
    width: DocumentColumnWidths.type
  },
  {
    name: 'version',
    header: 'Document Version',
    sortable: true,
    width: DocumentColumnWidths.version
  },
  {
    name: 'status',
    header: 'Status',
    sortable: true,
    centered: true,
    width: DocumentColumnWidths.status,
  }
];

export const MessagesColumnDefinitions: ColumnDefinition[] = [
  {
    name: 'messageSubject',
    header: 'Category',
    sortable: true,
    width: MessagesColumnWidths.category
  },
  {
    name: 'childSubject',
    header: 'Sub-Category',
    sortable: true,
    width: MessagesColumnWidths.subCategory
  },
  {
    name: 'lastMessageDate',
    header: 'Date Updated',
    sortable: true,
    width: MessagesColumnWidths.date
  }
];

export const EmployeesColumnDefinitions: ColumnDefinition[] = [
  {
    name: 'firstName',
    header: 'First Name',
    sortable: true,
    width: EmployeesColumnWidths.firstName
  },
  {
    name: 'lastName',
    header: 'Last Name',
    sortable: true,
    width: EmployeesColumnWidths.lastName
  },
  {
    name: 'type',
    header: 'Service',
    sortable: true,
    width: EmployeesColumnWidths.service
  },
  {
    name: 'subServices',
    header: 'Sub-Service',
    sortable: true,
    width: EmployeesColumnWidths.subService,
    type: "array"
  }
];

export const ContactsColumnDefinitions: ColumnDefinition[] = [
  {
    name: 'firstName',
    header: 'First Name',
    sortable: true,
    width: 12
  },
  {
    name: 'lastName',
    header: 'Last Name',
    sortable: true,
    width: 12
  },
  {
    name: 'title',
    header: 'Job Title/Position',
    sortable: true,
    width: 13
  },
  {
    name: 'email',
    header: 'Email Address',
    sortable: true,
    width: 19
  },
  {
    name: 'phone',
    header: 'Phone No.',
    sortable: true,
    width: 13
  },
    {
    name: 'statusText',
    header: 'Status',
    sortable: true,
    width: 2
  },
  {
    name: 'isHDMA',
    header: 'HDMA',
    sortable: true,
    width: 5
  },
  {
    name: 'isPrimaryContact',
    header: 'Primary',
    sortable: true,
    width: 5
  },
  {
    name: 'actions',
    header: 'Action',
    sortable: false,
    width: 12
  }
];

export const IssueTrackingColumnDefinitions: ColumnDefinition[] = [
  {
    name: 'chevron',
    header: '',
    sortable: false,
    width: IssueTrackingColumnWidths.chevron,
    expandedWidth: IssueTrackingColumnWidths.chevron,
    toggleExpand: true
  },
  {
    name: 'dateReported',
    header: 'Date Reported',
    sortable: true,
    width: IssueTrackingColumnWidths.date,
    expandedWidth: IssueTrackingColumnWidths.date,
    expandable: true
  },
  {
    name: 'onHoldStatus',
    header: 'Restrictions',
    sortable: true,
    width: IssueTrackingColumnWidths.onHoldStatus,
    expandedWidth: IssueTrackingColumnWidths.onHoldStatus,
    expandable: true
  },
  {
    name: 'body',
    header: 'Details',
    sortable: true,
    width: IssueTrackingColumnWidths.comments,
    expandedWidth: IssueTrackingColumnWidths.comments,
    expandable: true
  },
  {
    name: 'createdByName',
    header: 'Issued By',
    sortable: true,
    width: IssueTrackingColumnWidths.issuedBy,
    expandedWidth: IssueTrackingColumnWidths.issuedBy,
    expandable: true
  },
  {
    name: 'issueTrackingAction',
    header: 'Action',
    sortable: true,
    centered: true,
    width: IssueTrackingColumnWidths.action,
    expandedWidth: IssueTrackingColumnWidths.action,
    expandable: true,
    action: true
  }
];

export const StudentsColumnDefinitions: ColumnDefinition[] = [
  {
    name: 'studentName',
    header: 'Student(s)',
    sortable: true,
    width: StudentsDataColumnWidths.studentName
  },
  {
    name: 'pidNumber',
    header: 'PID Number',
    sortable: true,
    centered: true,
    width: StudentsDataColumnWidths.pidNumber
  },
  {
    name: 'grade',
    header: 'Grade Level',
    sortable: true,
    centered: true,
    width: StudentsDataColumnWidths.grade
  },
  {
    name: 'osAwardAmountOffered',
    header: 'Opportunity Scholarship Award',
    sortable: true,
    centered: true,
    width: StudentsDataColumnWidths.osAwardAmountOffered
  },
  {
    name: 'esaAwardAmountOffered',
    header: 'ESA+ Award',
    sortable: true,
    centered: true,
    width: StudentsDataColumnWidths.esaAwardAmountOffered
  },
  {
    name: 'studentStatus',
    header: 'Status',
    sortable: true,
    centered: true,
    width: StudentsDataColumnWidths.studentStatus
  },
  {
    name: 'schoolName',
    header: 'School Name',
    sortable: true,
    width: StudentsDataColumnWidths.schoolName
  }
];